import { Typography, useTheme, Tooltip } from '@mui/material';
import {
  type GridRowData,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { colorTheme } from 'theme';
import { useEffect, useState } from 'react';
import { exportFundDetails, viewAllFundDetails } from 'service';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { fundDetailsConstants } from 'constants/constantText';
import { strings } from 'i18n/Strings';
// import { tofixedNumber } from 'utils/GeneralUtil';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import { UnitFormater } from 'utils/GeneralUtil';

export interface FundDetailsListInterface {
  description: string;
  fundId: number;
  fundName: string;
  totalUnits: number;
  symbol: string;
  contractId: string;
  todayPrice: number;
  clientUnits: number;
  mmUnits: number;
}

const FundDetails = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [fundData, setFundData] = useState<FundDetailsListInterface[]>([]);
  const [filterFundData, setFilterFundData] = useState<
    FundDetailsListInterface[]
  >([]);

  const [searchValue, setSearchValue] = useState('');

  const dispatch = useDispatch();
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.fundDetails);
  const handleExport = (): void => {
    exportFundDetails(fundData)
      .then((res) => {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.exportFundDetailsSuccess,
          }),
        );
      })
      .catch((err) => {
        dispatch(
          setSnackBarFailed({
            snackBarMessage: err?.response?.data?.message,
          }),
        );
      });
  };

  useEffect(() => {
    viewAllFundDetails(Number(pageInfo?.page) + 1, pageInfo?.pageSize)
      .then((res) => {
        setFundData(res?.result?.fundDetails);
        setPageCount(res?.result?.count);
      })
      .catch((err) => {
        console.log('error viewAllFundDetails', err);
      });
  }, [pageInfo?.page, pageInfo?.pageSize]);

  useEffect(() => {
    const filter =
      searchValue !== ''
        ? fundData.filter(
            (fund) =>
              fund.fundName.toLowerCase().includes(searchValue.toLowerCase()) ||
              fund.contractId.toString().includes(searchValue) ||
              fund.description
                .toLowerCase()
                .includes(searchValue.toLowerCase()) ||
              fund.symbol.toLowerCase().includes(searchValue.toLowerCase()) ||
              fund.fundId.toString().includes(searchValue),
          )
        : fundData;

    setFilterFundData(filter);
  }, [searchValue, fundData]);

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: fundDetailsConstants.fundNameField,
      width: 350,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundDetailsFundNameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row?.description} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row?.shortDescription}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: fundDetailsConstants.totalUnitsField,
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundDetailsFundTotalUnitsColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {UnitFormater(Number(row?.totalUnits))}
        </Typography>
      ),
    },
    {
      field: 'Symbol',
      width: 150,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.symbol}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.symbol}
        </Typography>
      ),
    },
    {
      field: 'contractId',
      width: 120,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.contractId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.contractId}
        </Typography>
      ),
    },
    {
      field: fundDetailsConstants.unitsValueField,
      width: 150,
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundDetailsUnitsValueColumn}
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row?.todayPrice) * Number(row?.totalUnits))}
        </Typography>
      ),
    },
    {
      field: fundDetailsConstants.clientUnitsField,
      width: 150,
      cellClassName: 'name-column--cell',
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundDetailsClientUnitsColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {UnitFormater(Number(row?.clientUnits))}
        </Typography>
      ),
    },
    {
      field: fundDetailsConstants.mmUnitsField,
      width: 150,
      align: 'right',
      headerAlign: 'center',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.fundDetailsMMUnitsColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography
          className="MuiTypography-alignRight"
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
        >
          {UnitFormater(Number(row?.mmUnits))}
        </Typography>
      ),
    },
    {
      field: fundDetailsConstants.closePrice,
      width: 150,
      align: 'right',
      headerAlign: 'right',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.closePrice}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {CurrencyFormater(Number(row?.todayPrice).toFixed(2))}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <CustomDataGrid
        columns={columns}
        rows={filterFundData as GridRowData[]}
        getRowId={(row: GridRowData) => `${String(row.fundName)}`}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        isShowExport={true}
        currentPage="fundDetails"
        onExportClick={handleExport}
        pageCount={pageCount}
        searchEnabled={true}
        handleSearch={handleSearch}
        searchPlaceholder={strings.displayText.searchLabel4}
      />
    </>
  );
};

export default FundDetails;
